import { ensureJqueryEl } from "../../utilities/helpers";
import { initTooltips } from "../../core/initilizers/tooltips";
import { initPopovers } from "../../core/initilizers/popovers";
/**
 * A class to manage the size selection and display UI.
 * This class encapsulates all the logic related to a single size container 
 * in the produst configurartions form,replacing the global functions and 
 * variables from the original script.
 * @class
 */
export class ProductSizeConfig {

    /**
     * The jQuery object for the root container element.
     * @private
     * @type {jQuery}
     */
    $container;

    /**
     * Creates an instance of ProductSizeConfig.
     * @param {HTMLElement} container - The DOM element that serves as the root container for this component.
     */
    constructor($container, isCalledByGetInstance = false) {
        if (!isCalledByGetInstance) {
            throw new Error("ProductSizeConfig: Use getInstance() to create instances.");
        }

        this.$container = $container;
        this.bindEvents();

        // Call the initial setup once on load
        this.updateUI();
    }

    /**
     * @static
     * @method getInstance
     * @description Gets or creates a single instance of ProductSizeConfig for a given container.
     * @param {string|jQuery|HTMLElement} container - The container element, jQuery object, or selector string.
     * @returns {ProductSizeConfig} The single instance of the class for the provided container.
     */
    static getInstance(container) {
        const $container = ensureJqueryEl(container);

        if (!$container.length) {
            console.warn("ProductSizeConfig: Container element not found.");
            return;
        }

        let instance = $container.data('ProductSizeConfig');

        if (!instance) {
            instance = new ProductSizeConfig($container, true);
            $container.data('ProductSizeConfig', instance);
        }

        return instance;
    }


    /**
     * Binds all event listeners for the component's interactive elements.
     * @private
     */
    bindEvents() {
        this.$container.find('.width, .height').on('change', (event) => {
            const input = $(event.target);
            const decimalValue = this.convertToDecimalForm(input.val());

            if (isFinite(decimalValue)) {
                input.val(decimalValue);
            } else {
                input.val('');
            }
            this.updateUI(event);
        });

        this.$container.find('select.size').on('change', (event) => {
            this.updateUI(event);
        });

        this.$container.closest('.product-configurations').find('select.product-selection').on('change', (event) => {
            this.updateUI(event);
        });

        this.$container.find('.landscape label').on('click', () => {
            this.orientInputs('landscapeInput');
        });

        this.$container.find('.portrait label').on('click', () => {
            this.orientInputs('portraitInput');
        });

        this.$container.find('.setViewSize').on('click', (event) => {
            const isChecked = $(event.target).prop('checked');
            this.toggleFields(this.$container.find('.viewSize'), isChecked);
        });

        this.$container.find('.js-unknown-dimensions').on('change', (event) => {
            this.handleUnknownDimensionsChange(event.target);
        });
    }

    // --- Core Logic ---

    /**
     * A master function to handle all UI updates in a single pass.
     * It ensures that the orientation diagram, max view size, and other
     * UI elements are always in sync with the current input values.
     * @private
     */
    updateUI = (event = null) => {
        const size = this.getSize();
        this.orientDiagram(size);
        this.setMaxViewSize(size);
        this.syncViewSizeCheckbox(event);
        initTooltips();
        initPopovers();
    }

    /**
     * Orients the width and height inputs based on a chosen orientation.
     * It swaps the input values if necessary and then triggers a UI update.
     * @param {string} orientation - The desired orientation ('landscapeInput' or 'portraitInput').
     */
    orientInputs = (orientation) => {
        const widthField = this.$container.find('.width');
        const heightField = this.$container.find('.height');

        let size = this.getCustomSize(widthField, heightField);
        size = (orientation === this.getOrientation(size)) ? size : this.swap(size);
        this.setCustomSize(size, widthField, heightField);

        this.swapViewSize();
        this.updateUI();
    }

    // --- Helper Methods ---

    /**
     * Toggles the visibility and enabled state of a set of fields.
     * @param {jQuery} fields - The jQuery object containing the fields to toggle.
     * @param {boolean} show - True to show the fields, false to hide them.
     */
    toggleFields = (fields, show) => {
        show ?
            fields.slideDown().prop('disabled', false) :
            fields.slideUp().prop('disabled', true);
    }

    /**
     * Determines the orientation of a given size object.
     * @param {object} size - An object with `width` and `height` properties.
     * @returns {string} The orientation as 'landscapeInput' or 'portraitInput'.
     */
    getOrientation = (size) => {
        return size.width > size.height ? 'landscapeInput' : 'portraitInput';
    }

    /**
     * Swaps the width and height properties of a size object.
     * @param {object} size - An object with `width` and `height` properties.
     * @returns {object} A new size object with the dimensions swapped.
     */
    swap = (size) => {
        return {
            width: size.height,
            height: size.width
        };
    }

    /**
     * Swaps the values of the view size inputs.
     */
    swapViewSize = () => {
        const viewWidth = this.$container.find('.viewWidth');
        const viewHeight = this.$container.find('.viewHeight');

        const viewSize = this.swap({
            width: +viewWidth.val() || 0,
            height: +viewHeight.val() || 0
        });

        viewWidth.val(viewSize.width);
        viewHeight.val(viewSize.height);
    }

    /**
     * Updates the checked state of the orientation radio buttons based on the size.
     * @param {object} size - An object with `width` and `height` properties.
     */
    orientDiagram = (size) => {
        const orientation = this.getOrientation(size);
        const targetInput = this.$container.find(`.${orientation}`);

        $(targetInput).prop('checked', true);
    }

    /**
     * Retrieves the current size from the width and height inputs.
     * @returns {object} A size object with `width` and `height` properties.
     */
    getSize = () => {
        return this.getCustomSize(this.$container.find('.width'), this.$container.find('.height'));
    }

    /**
     * Gets the size from specified width and height input fields.
     * @param {jQuery} widthField - The jQuery object for the width input.
     * @param {jQuery} heightField - The jQuery object for the height input.
     * @returns {object} A size object with `width` and `height` properties.
     */
    getCustomSize = (widthField, heightField) => {
        const width = parseFloat(widthField.val()) || 0;
        const height = parseFloat(heightField.val()) || 0;
        return {
            width,
            height
        };
    }

    /**
     * Sets the values of the custom size inputs.
     * @param {object} size - The size object to set.
     * @param {jQuery} widthField - The jQuery object for the width input.
     * @param {jQuery} heightField - The jQuery object for the height input.
     */
    setCustomSize = (size, widthField, heightField) => {
        widthField.val(size.width);
        heightField.val(size.height);
    }

    /**
     * Sets the `max` attribute for the view size inputs.
     * @param {object} size - The size object to use for the max values.
     */
    setMaxViewSize = (size) => {
        this.$container.find('.viewWidth').attr('max', size.width);
        this.$container.find('.viewHeight').attr('max', size.height);
    }

    /**
     * Converts a size string (e.g., "1 1/2") to a decimal.
     * @param {string} size - The size string.
     * @returns {number} The decimal value of the size.
     */
    convertToDecimalForm = (size) => {
        const sizeArray = size.split(' ');

        if (sizeArray.length > 1) {
            const fraction = sizeArray[1].split('/');
            return +sizeArray[0] + (fraction[0] / fraction[1]);
        } else {
            const fraction = sizeArray[0].split('/');
            if (fraction.length > 1) {
                return fraction[0] / fraction[1];
            } else {
                return (+fraction[0]);
            }
        }
    }

    syncViewSizeCheckbox = (event) => {
        const size = this.getSize();
        const $triggerEl = $(event?.currentTarget);

        const $viewSizeCheckbox = this.$container.find('.setViewSize');
        const $viewSizeWrap = this.$container.find('.set-viewsize');

        const $widthInput = this.$container.find('input.width');
        const $heightInput = this.$container.find('input.height');
        const areNoInputs = !$widthInput.length && !$heightInput.length;

        const isSelectEl = $triggerEl.is('select');
        const isSelectSizeNotCustom = isSelectEl && $triggerEl.hasClass('.size') && $triggerEl.val() !== "";
        const isProductSelectNoValue = isSelectEl && $triggerEl.hasClass('.product-selection') && $triggerEl.val() === "";

        const isSizeProvided = size.width > 0 && size.height > 0;

        if (isSizeProvided || areNoInputs || isSelectSizeNotCustom || isProductSelectNoValue) {
            $viewSizeWrap.slideDown();
            $viewSizeCheckbox.prop('disabled', false);
        } else {
            $viewSizeWrap.slideUp();
            $viewSizeCheckbox.prop('disabled', true);
        }

        if (!isSizeProvided && $viewSizeCheckbox.prop('checked')) {
            $viewSizeCheckbox.prop('checked', false);
            this.toggleFields(this.$container.find('.viewSize'), false);
        }
    }

    handleUnknownDimensionsChange(el) {
        const $el = $(el);
        const isChecked = $el.is(':checked');
        const orderHasExistingSurvey = $el.attr('data-existing-survey') === 'true';

        // Scope to the full product-configurations block so it affects sibling size containers,
        const $productConfigurationRoot = this.$container.closest('.product-configurations').length
            ? this.$container.closest('.product-configurations')
            : $(document);

        const $currentWrapper = $el.closest('.unknown-dimensions-wrapper');
        const $feedbackBox = $(`#${$el.attr('aria-controls')}`);
        const $allSizeContainers = $productConfigurationRoot.find('.sizeContainer');
        const $dimensionFields = $allSizeContainers.find('.size');

        // View size section (wrapper + its inputs) exists per container
        const $allViewSizeWraps = $allSizeContainers.find('.set-viewsize');
        const $allViewSizeInputs = $allSizeContainers.find('input.viewWidth, input.viewHeight');
        const $allViewSizeCheckboxes = $allSizeContainers.find('input.setViewSize');

        const $allUnknownWrappers = $allSizeContainers.find('.unknown-dimensions-wrapper');

        if (!orderHasExistingSurvey) {
            // Prevent double-submission
            if ($el.prop('disabled')) return;

            // Show pending state
            $el.prop('disabled', true);
            $feedbackBox.removeClass('d-none alert-danger').addClass('alert-info').text('Updating order...');

            this.updateSiteSurveyInOrder(isChecked, $el, $feedbackBox, $dimensionFields, $allViewSizeWraps, $allViewSizeCheckboxes, $allViewSizeInputs, $allUnknownWrappers, $currentWrapper, orderHasExistingSurvey);
        } else {
            this.applyUIChangesFromSurveyPrompt(isChecked, $el, $feedbackBox, $dimensionFields, $allViewSizeWraps, $allViewSizeCheckboxes, $allViewSizeInputs, $allUnknownWrappers, $currentWrapper, orderHasExistingSurvey);
        }
    }

    applyUIChangesFromSurveyPrompt(isChecked, $el, $feedbackBox, $dimensionFields, $allViewSizeWraps, $allViewSizeCheckboxes, $allViewSizeInputs, $allUnknownWrappers, $currentWrapper, orderHasExistingSurvey) {
        if (isChecked) {
            // Remember previous dimension values once, then set to 0 and disable
            $dimensionFields.each((_, field) => {
                const $field = $(field);

                if ($field.data('prev-value') === undefined) {
                    $field.data('prev-value', $field.val());
                }

                $field.val(0).prop('disabled', true).addClass('is-greyed-out');
            });

            // Show the informational feedback for the active checkbox
            const message = orderHasExistingSurvey
                ? 'We will update these dimensions after the survey is complete.'
                : '<b>Site Survey added to order.</b> We will update these dimensions after the survey is complete.';
            $feedbackBox.removeClass('d-none alert-danger').addClass('alert-info').html(message);

            // Hide ALL view size sections, clear values, and uncheck toggles
            $allViewSizeWraps.hide();
            $allViewSizeCheckboxes.prop('checked', false).prop('disabled', true);
            $allViewSizeInputs.val('');

            // Hide all unknown-dimensions wrappers except the current one
            $allUnknownWrappers.not($currentWrapper).hide();
            $allUnknownWrappers.not($currentWrapper).find('.js-unknown-dimensions').prop('checked', false);
        } else {
            // Re-enable and restore previous values (or blank if none)
            $dimensionFields.each((_, field) => {
                const $field = $(field);
                const prev = $field.data('prev-value');

                $field.prop('disabled', false).removeClass('is-greyed-out');

                $field.val(prev !== undefined ? prev : '');
                $field.removeData('prev-value');
            });

            $feedbackBox.addClass('d-none').removeClass('alert-danger').addClass('alert-info');
            $allUnknownWrappers.show();
            $allViewSizeCheckboxes.prop('disabled', false);

            // decide whether viewsize is allowed/shown now
            this.updateUI();
        }
    }

    updateSiteSurveyInOrder = (isAdd = false, $el, $feedbackBox, $dimensionFields, $allViewSizeWraps, $allViewSizeCheckboxes, $allViewSizeInputs, $allUnknownWrappers, $currentWrapper, orderHasExistingSurvey = false) => {
        const siteSurveyProductId = $el.attr('data-survey-id');
        const siteSurveyProductData = {
            productRootID: siteSurveyProductId,
            variantRootID: '',
            quantity: isAdd ? 1 : 0
        };

        const updateQtyRoute = window.app.routes.batchQuickAdd;
        const token = window.app.config.csrfToken;

        $.ajax({
            url: updateQtyRoute,
            method: 'POST',
            headers: {'X-CSRF-TOKEN': token},
            data: {'batch-data': [siteSurveyProductData]}
        })
            .done((response) => {
                const parsedResponse = JSON.parse(response);
                $el.prop('disabled', false);

                if (parsedResponse.status === 'Success') {
                    this.applyUIChangesFromSurveyPrompt(isAdd, $el, $feedbackBox, $dimensionFields, $allViewSizeWraps, $allViewSizeCheckboxes, $allViewSizeInputs, $allUnknownWrappers, $currentWrapper, orderHasExistingSurvey);
                } else {
                    $el.prop('checked', !isAdd);
                    $feedbackBox.removeClass('d-none alert-info').addClass('alert-danger').text('Failed to update survey: ' + (parsedResponse.message || 'Unknown error'));
                }
            })
            .fail((xhr, status, error) => {
                $el.prop('disabled', false).prop('checked', !isAdd);
                $feedbackBox.removeClass('d-none alert-info').addClass('alert-danger').text('Error updating survey. Please try again.');
                console.error("AJAX Error:", status, error);
            });
    }
}

/**
 * @function initProductSizeConfigs
 * @description Finds all elements with the '.sizeContainer' class and initializes a new ProductSizeConfig instance for each.
 */
const initProductSizeConfig = () => {
    $('.sizeContainer').each((_, el) => {
        ProductSizeConfig.getInstance(el);
    });

    $('.survey-tooltip').on('click mousedown', function(e) {
        e.preventDefault();
        e.stopPropagation();
    });
}
export default initProductSizeConfig;